import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ElementRef,
  ViewChild,
  computed,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  Attachment,
  AttachmentPayload,
  MediaApiService,
  MediaType,
  WorkspaceService,
  ProfileService,
  SignedUrlUploadResult,
  SignedUrlUploadService,
  Account,
} from '@doddly/core';
import { Observable } from 'rxjs';
import { IconComponent } from '../icon/icon.component';
import { ConfirmDirective } from '../confirm-popover/confirm.directive';
import { MessageService } from 'primeng/api';

@Component({
  selector: 'ly-media-uploader',
  standalone: true,
  imports: [CommonModule, IconComponent, ConfirmDirective],
  templateUrl: './media-uploader.component.html',
  styleUrl: './media-uploader.component.scss',
})
export class MediaUploaderComponent implements OnChanges {
  currentAttachmentsLength = 0;
  readonly MEDIA_TYPE = MediaType;
  @Output() media = new EventEmitter<Attachment[]>();

  @Input()
  account!: Account;

  @Input()
  attachments: Attachment[] = [];

  @Input()
  buttonType: 'fluid' | '' = '';

  @Input()
  size: 'small' | '' = '';

  @Input()
  disabled = false;

  @Input()
  fileMaxSize = 10 * 1024 * 1024; // 10MB by default

  processing = false;

  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;

  profile = computed(() => this.profileService.profile());

  constructor(
    private mediaApiService: MediaApiService,
    private workspaceService: WorkspaceService,
    private signedUrlUploadService: SignedUrlUploadService,
    private profileService: ProfileService,
    private messageService: MessageService,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['attachments']) {
      this.attachments = changes['attachments'].currentValue as Attachment[];
      this.currentAttachmentsLength = this.attachments.length;
    }
  }

  get isPlanExpired(): boolean {
    return (
      new Date(this.account.plan.expiresAt).getTime() < new Date().getTime()
    );
  }

  createAttachment(payload: AttachmentPayload): Observable<Attachment> {
    return this.mediaApiService.createAttachment(payload);
  }

  openFilePicker() {
    if (this.isPlanExpired) {
      this.showPlanExpiredError();
      return;
    }

    this.fileInput.nativeElement.click();
  }

  onFileSelected(event: Event) {
    const target = event.target as HTMLInputElement;
    const files = target.files;

    if (!files || files.length === 0) {
      return;
    }

    this.processing = true;
    const fileArray = Array.from(files);
    let uploadedCount = 0;
    const totalFiles = fileArray.length;
    const workspaceId = this.workspaceService.activeWorkspace().id;

    if (this.checkIfFilesDoNotExceedsMaxSize(fileArray)) {
      this.processing = false;
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        life: 6000,
        detail: `Oops! One file was too big, so we had to stop the lot. Try again with something slimmer! You can see the upload limits in our pricing section.`,
      });
      target.value = '';
      return;
    }

    fileArray.forEach((file) => {
      // Use the new signed-URL upload approach
      this.signedUrlUploadService
        .uploadFile(file, workspaceId, false)
        .subscribe({
          next: (result: SignedUrlUploadResult) => {
            const attachment = this.mapToAttachment(result);
            this.createAttachment(attachment).subscribe((attachment) => {
              this.attachments.push(attachment);
              uploadedCount++;

              // emit the attachments when all files are uploaded
              if (uploadedCount === totalFiles) {
                this.media.emit([...this.attachments]);
                this.processing = false;
              }
            });
          },
          error: (error: any) => {
            console.error('Upload failed:', error);
            uploadedCount++;
            if (uploadedCount === totalFiles) {
              this.processing = false;
            }
          },
        });
    });

    // Reset the file input
    target.value = '';
  }

  private mapToAttachment(result: SignedUrlUploadResult): AttachmentPayload {
    const media: AttachmentPayload = {
      workspaceId: this.workspaceService.activeWorkspace().id,
      URL: result.downloadURL, // Use the downloadURL from signed URL response
      height: result.height || 0,
      width: result.width || 0,
      thumbnailURL: result.downloadURL,
      type: this.getMediaType(result.type),
      mime: result.type,
      name: result.name,
      size: result.size,
    };
    return media;
  }

  private getMediaType(mimeType: string): string {
    if (mimeType.startsWith('image/')) {
      return MediaType.image;
    } else if (mimeType.startsWith('video/')) {
      return MediaType.video;
    }
    return MediaType.image; // Default fallback
  }

  private checkIfFilesDoNotExceedsMaxSize(files: File[]): boolean {
    return files.some((file) => file.size > this.fileMaxSize);
  }

  private showPlanExpiredError(): void {
    this.messageService.add({
      severity: 'error',
      summary: 'Error',
      life: 6000,
      detail:
        'Your plan has expired. Please renew your plan to continue uploading media.',
    });
  }
}
